/**
 * Footer PPHU Imperium
 * Design: Industrial Precision granatowe tło, NAP zgodny z Google
 * NAP: Name, Address, Phone spójne z wizytówką Google
 */
import { Link } from 'wouter';
import { Phone, Mail, MapPin, ExternalLink } from 'lucide-react';
import { companyData } from '@/theme';
import { trpc } from '@/lib/trpc';

const navLinks = [
  { label: 'Strona główna', href: '/' },
  { label: 'Oferta', href: '/oferta' },
  { label: 'Kontakt', href: '/kontakt' },
  { label: 'Polityka prywatności', href: '/polityka-prywatnosci' },
];

const productCategories = [
  { label: 'Elektronarzędzia Milwaukee', id: 'kat-0' },
  { label: 'Pneumatyka', id: 'kat-1' },
  { label: 'Narzędzia warsztatowe', id: 'kat-2' },
  { label: 'Materiały ścierne', id: 'kat-3' },
  { label: 'Narzędzia skrawające', id: 'kat-4' },
  { label: 'Narzędzia pomiarowe', id: 'kat-5' },
  { label: 'Chemia techniczna', id: 'kat-6' },
  { label: 'Materiały spawalnicze', id: 'kat-7' },
  { label: 'Śruby i łożyska', id: 'kat-8' },
];

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const cmsPages = trpc.cms.public.pages.useQuery();
  const allNavLinks = [
    ...navLinks,
    ...(cmsPages.data ?? []).map(page => ({ label: page.title, href: `/strona/${page.slug}` })),
  ];

  return (
    <footer
      style={{
        backgroundColor: '#111B30',
        borderTop: '3px solid #F5C400',
        color: '#F8F8F6',
      }}
      itemScope
      itemType="https://schema.org/LocalBusiness"
    >
      {/* Main footer content */}
      <div className="container" style={{ paddingTop: '3.5rem', paddingBottom: '2.5rem' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '2.5rem',
        }}>

          {/* Column 1: Brand + NAP */}
          <div>
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 800,
                fontSize: '1.5rem',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: '#F5C400',
                marginBottom: '0.25rem',
              }}>
                <span itemProp="name">PPHU Imperium</span>
              </div>
              <div style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: '0.8rem',
                color: 'rgba(248,248,246,0.5)',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}>
                Robert Gostomczyk
              </div>
            </div>

            <p style={{
              fontSize: '0.875rem',
              color: 'rgba(248,248,246,0.65)',
              lineHeight: 1.7,
              marginBottom: '1.5rem',
              maxWidth: '260px',
            }}>
              Sklep z elektronarzędziami, narzędziami warsztatowymi i elementami złącznymi w Sławnie. Autoryzowany punkt sprzedaży Milwaukee.
            </p>

            {/* NAP Name Address Phone */}
            <address style={{ fontStyle: 'normal' }}>
             <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <a
                  href={companyData.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'flex', alignItems: 'flex-start', gap: '0.625rem', textDecoration: 'none' }}
                >
                  <MapPin size={15} style={{ color: '#F5C400', flexShrink: 0, marginTop: '2px' }} />
                  <span
                    style={{ fontSize: '0.875rem', color: 'rgba(248,248,246,0.75)', lineHeight: 1.5 }}
                    itemProp="address"
                    itemScope
                    itemType="https://schema.org/PostalAddress"
                  >
                    <span itemProp="streetAddress">{companyData.address.street}</span>,{' '}
                    <span itemProp="addressLocality">{companyData.address.city}</span>
                  </span>
                </a>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                  <Phone size={15} style={{ color: '#F5C400', flexShrink: 0 }} />
                  <a
                    href={`tel:${companyData.phoneRaw}`}
                    style={{ fontSize: '0.875rem', color: 'rgba(248,248,246,0.75)', textDecoration: 'none' }}
                    itemProp="telephone"
                  >
                    {companyData.phone}
                  </a>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
                  <Mail size={15} style={{ color: '#F5C400', flexShrink: 0 }} />
                  <a
                    href={`mailto:${companyData.email}`}
                    style={{ fontSize: '0.875rem', color: 'rgba(248,248,246,0.75)', textDecoration: 'none' }}
                    itemProp="email"
                  >
                    {companyData.email}
                  </a>
                </div>
              </div>
            </address>
          </div>

          {/* Column 2: Navigation */}
          <div>
            <h3 style={{
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 700,
              fontSize: '0.75rem',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: '#F5C400',
              marginBottom: '1.25rem',
              paddingBottom: '0.5rem',
              borderBottom: '1px solid rgba(245,196,0,0.2)',
            }}>
              Nawigacja
            </h3>
            <nav>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
               {allNavLinks.map((link) => (
                 <li key={link.href}>
                   <Link
                     href={link.href}
                     style={{
                       fontFamily: "'Manrope', sans-serif",
                       fontSize: '0.875rem',
                       color: 'rgba(248,248,246,0.65)',
                       textDecoration: 'none',
                       transition: 'color 150ms ease',
                     }}
                     onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#F5C400'; }}
                     onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(248,248,246,0.65)'; }}
                      onClick={() => { window.scrollTo({ top: 0, behavior: 'instant' }); }}
                   >
                     {link.label}
                   </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Column 3: Product categories */}
          <div>
            <h3 style={{
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 700,
              fontSize: '0.75rem',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: '#F5C400',
              marginBottom: '1.25rem',
              paddingBottom: '0.5rem',
              borderBottom: '1px solid rgba(245,196,0,0.2)',
            }}>
              Asortyment
            </h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {productCategories.map((cat) => (
               <li key={cat.label} style={{
                 fontSize: '0.8rem',
                 color: 'rgba(248,248,246,0.55)',
                 fontFamily: "'Manrope', sans-serif",
                 paddingLeft: '0.75rem',
                 borderLeft: '2px solid rgba(245,196,0,0.25)',
               }}>
                <a
                    href={`/oferta#${cat.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.href = `/oferta#${cat.id}`;
                      setTimeout(() => {
                        const el = document.getElementById(cat.id);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }, 300);
                    }}
                    style={{
                      color: 'rgba(248,248,246,0.55)',
                      textDecoration: 'none',
                      cursor: 'pointer',
                      transition: 'color 150ms',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#F5C400')}
                    onMouseLeave={e => (e.currentTarget.style.color = 'rgba(248,248,246,0.55)')}
                  >
                    {cat.label}
                  </a>
               </li>
             ))}
            </ul>
          </div>

          {/* Column 4: Google Maps link + hours */}
          <div>
            <h3 style={{
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 700,
              fontSize: '0.75rem',
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: '#F5C400',
              marginBottom: '1.25rem',
              paddingBottom: '0.5rem',
              borderBottom: '1px solid rgba(245,196,0,0.2)',
            }}>
              Znajdź nas
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{
                backgroundColor: 'rgba(245,196,0,0.08)',
                border: '1px solid rgba(245,196,0,0.2)',
                borderRadius: '4px',
                padding: '1rem',
              }}>
                <div style={{ fontSize: '0.8rem', color: 'rgba(248,248,246,0.6)', marginBottom: '0.5rem', fontFamily: "'Manrope', sans-serif" }}>
                  Godziny otwarcia
                </div>
                <div style={{ fontSize: '0.875rem', color: '#F8F8F6', fontFamily: "'Manrope', sans-serif" }}>
                  Pon Pt: 08:00 15:45<br />Sb / Nd: Zamknięte
                </div>
              </div>
              <a
                href={companyData.googleBusinessUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.8rem',
                  color: '#F5C400',
                  textDecoration: 'none',
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                <ExternalLink size={13} />
                Wizytówka Google
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div style={{
        borderTop: '1px solid rgba(255,255,255,0.08)',
        padding: '1.25rem 0',
      }}>
        <div className="container" style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '0.5rem',
        }}>
          <p style={{
            fontSize: '0.75rem',
            color: 'rgba(248,248,246,0.35)',
            fontFamily: "'Manrope', sans-serif",
            margin: 0,
          }}>
            © {currentYear} PPHU Imperium Robert Gostomczyk · ul. Kosynierów 21, Sławno
          </p>
          <p style={{
            fontSize: '0.75rem',
            color: 'rgba(248,248,246,0.25)',
            fontFamily: "'Manrope', sans-serif",
            margin: 0,
          }}>
            Powered by:{' '}
            <a
              href="https://architekcibiznesu.pl"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'rgba(248,248,246,0.4)', textDecoration: 'none', fontWeight: 600 }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#F5C400'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(248,248,246,0.4)'; }}
            >
              Architekci Biznesu
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
